import { useCallback, useState } from "react";
import {
    ColorValue,
    LayoutChangeEvent,
    NativeScrollEvent,
    NativeSyntheticEvent,
    View,
} from "react-native";

type UseScrollHeaderColorSyncProps = {
    aboveLimitColor: ColorValue;
    belowLimitColor: ColorValue;
};

/**
 * This hook allows to easily synchronize the screen header + statusBar background colors when scrolling through a ScrollView
 * Typically, this is used where the first elements of a ScrollView have a certain background color `aboveLimitColor`, but the rest have another background color `belowLimitColor`.
 * This bring a better visual harmony between the header and the content when scrolling
 */
export const useScrollHeaderColorSync = ({
    aboveLimitColor,
    belowLimitColor,
}: UseScrollHeaderColorSyncProps) => {
    const [screenHeaderBackgroundColor, setScreenHeaderBackgroundColor] =
        useState<ColorValue>(aboveLimitColor);

    const [limitYPosition, setLimitYPosition] = useState<number>();

    const onLimitLayout = useCallback((event: LayoutChangeEvent) => {
        setLimitYPosition(event.nativeEvent.layout.y);
    }, []);

    const onScroll = useCallback(
        (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            if (limitYPosition) {
                if (event.nativeEvent.contentOffset.y < limitYPosition) {
                    if (screenHeaderBackgroundColor !== aboveLimitColor) {
                        setScreenHeaderBackgroundColor(aboveLimitColor);
                    }
                } else if (screenHeaderBackgroundColor !== belowLimitColor) {
                    setScreenHeaderBackgroundColor(belowLimitColor);
                }
            }
        },
        [aboveLimitColor, belowLimitColor, limitYPosition, screenHeaderBackgroundColor],
    );

    return {
        /**
         *  The current header background color, to give for exemple to `<ScreenHeader />`
         */
        headerBackgroundColor: screenHeaderBackgroundColor,
        /**
         *  Should be placed at the limit between the `aboveLimitColor` and `belowlimitColor`
         */
        limitComponent: <View onLayout={onLimitLayout} />,
        /**
         *  To pass to your `<ScrollView />`
         */
        onScroll,
    };
};
